import { useState, useCallback, useRef, useEffect } from "react";
import { Bluelagoon, useTaskWebSocket } from "@bluelagoon";

const TASK_ID_KEY = "integration_build_task_id";
const DRAFT_ID_KEY = "integration_build_draft_id";
const STATE_KEY = "integration_build_state";
const STATE_TTL = 30 * 60 * 1000; // 30 min — stale builds are abandoned

function loadPersistedState() {
    try {
        const raw = localStorage.getItem(STATE_KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (Date.now() - saved._ts > STATE_TTL) { localStorage.removeItem(STATE_KEY); return null; }
        return saved;
    } catch { return null; }
}

function persistState(state) {
    try { localStorage.setItem(STATE_KEY, JSON.stringify({ ...state, _ts: Date.now() })); } catch { /* quota */ }
}

function clearPersistedState() {
    localStorage.removeItem(STATE_KEY);
    localStorage.removeItem(TASK_ID_KEY);
    localStorage.removeItem(DRAFT_ID_KEY);
}

export function useIntegrationBuildTask({ editingIntegration } = {}) {
    const hasActiveTask = !!editingIntegration?.activeTaskId;
    const isEditing = !!editingIntegration && !hasActiveTask;
    const persisted = isEditing ? null : loadPersistedState();
    const [taskId, setTaskId] = useState(() => {
        if (hasActiveTask) return editingIntegration.activeTaskId;
        if (isEditing) return null;
        return localStorage.getItem(TASK_ID_KEY) || null;
    });
    const [phase, setPhase] = useState(() => {
        if (hasActiveTask) return editingIntegration.endpoints?.length > 0 ? "discovering" : "crawling";
        if (isEditing) return "auth";
        if (persisted?.phase) return persisted.phase;
        return taskId ? "crawling" : "url_input";
    });
    const [apiType, setApiType] = useState(editingIntegration?.apiType || persisted?.apiType || "rest");
    const [endpoints, setEndpoints] = useState(() => {
        if (!isEditing && !hasActiveTask && persisted?.endpoints?.length) return persisted.endpoints;
        if (!isEditing && !hasActiveTask) return [];
        return (editingIntegration.endpoints || []).map((ep, i) => ({ ...ep, id: ep.id || `ep_edit_${i}_${ep.method}_${ep.path}` }));
    });
    const [revealedEndpoints, setRevealedEndpoints] = useState(() => {
        if (!isEditing && !hasActiveTask && persisted?.endpoints?.length) return persisted.endpoints.length;
        return (isEditing || hasActiveTask) ? (editingIntegration.endpoints || []).length : 0;
    });
    const [auth, setAuth] = useState(editingIntegration?.auth || persisted?.auth || null);
    const [errorMap, setErrorMap] = useState(editingIntegration?.errorMap || persisted?.errorMap || []);
    const [revealedErrors, setRevealedErrors] = useState(() => {
        if (!isEditing && !hasActiveTask && persisted?.errorMap?.length) return persisted.errorMap.length;
        return (isEditing || hasActiveTask) ? (editingIntegration.errorMap || []).length : 0;
    });
    const [chatMessages, setChatMessages] = useState(() => {
        if (hasActiveTask) return [{ role: "ai", text: `Reconnecting to build for **${editingIntegration.name}**...` }];
        if (isEditing) return [{ role: "ai", text: `Editing **${editingIntegration.name}**. Enter credentials and re-test endpoints.` }];
        if (persisted?.chatMessages?.length) return persisted.chatMessages;
        return [{ role: "ai", text: "Paste an API documentation URL and I'll build a complete integration for you." }];
    });
    const [integration, setIntegration] = useState(editingIntegration || persisted?.integration || null);
    const [draftId, setDraftId] = useState(() => {
        if (hasActiveTask || isEditing) return editingIntegration.id || null;
        return localStorage.getItem(DRAFT_ID_KEY) || persisted?.draftId || null;
    });
    const [error, setError] = useState(null);
    const [taskStartedAt, setTaskStartedAt] = useState(() => {
        if (hasActiveTask) return persisted?.taskStartedAt || Date.now();
        if (isEditing) return null;
        return persisted?.taskStartedAt || (taskId ? Date.now() : null);
    });
    const [isRecovering, setIsRecovering] = useState((!!persisted && !!taskId) || hasActiveTask);
    const feedbackIdx = useRef(persisted?.feedbackIdx || 0);
    const didCatchUp = useRef(false);

    const addChat = useCallback((msgs) => {
        setChatMessages((prev) => [...prev, ...(Array.isArray(msgs) ? msgs : [msgs])]);
    }, []);

    const processFeedback = useCallback((entry) => {
        if (!entry?.type) return;
        if (entry.type === "api_info" && entry.data?.name) {
            addChat({ role: "ai", text: `Found API: **${entry.data.name}** (${entry.data.apiType}) at \`${entry.data.baseUrl}\`` });
            setApiType(entry.data.apiType || "rest");
            setIntegration((prev) => ({ ...prev, name: entry.data.name, apiType: entry.data.apiType, baseUrl: entry.data.baseUrl, description: entry.data.description, logoUrl: entry.data.logoUrl || prev?.logoUrl }));
            setPhase("discovering");
        } else if (entry.type === "endpoints") {
            const newEps = (entry.data || []).filter((ep) => ep?.method && ep?.path).map((ep, i) => ({ ...ep, id: `ep_${Date.now()}_${i}_${ep.method}_${ep.path}` }));
            setEndpoints((prev) => {
                const existing = new Set(prev.map((e) => `${e.method} ${e.path}`));
                const updated = [...prev, ...newEps.filter((e) => !existing.has(`${e.method} ${e.path}`))];
                setTimeout(() => setRevealedEndpoints(updated.length), 100);
                return updated;
            });
            if (entry.total) addChat({ role: "ai", text: `Discovered ${entry.total} endpoints so far...` });
        } else if (entry.type === "auth") {
            setAuth(entry.data);
            setPhase("auth");
            addChat({ role: "ai", text: `Detected authentication: **${entry.data?.type || "unknown"}**` });
        } else if (entry.type === "errors") {
            setErrorMap((prev) => { const u = [...prev, ...(entry.data || [])]; setTimeout(() => setRevealedErrors(u.length), 100); return u; });
        } else if (entry.type === "complete" && entry.data) {
            setIntegration(entry.data);
        } else if (entry.type === "chat" && entry.text) {
            addChat({ role: "ai", text: entry.text });
        }
    }, [addChat]);

    const handleTaskUpdate = useCallback((update) => {
        const data = update.data || update;
        const feedback = data.partialFeedback || [];
        for (let i = feedbackIdx.current; i < feedback.length; i++) processFeedback(feedback[i]);
        feedbackIdx.current = feedback.length;
        // getTaskStatusAll spreads the task output at the top level of `data`, so the
        // integration lives at `data.integration`. Keep `data.output?.integration` as a
        // defensive fallback in case any path still nests it.
        const completedIntegration = data.integration || data.output?.integration;
        if (data.status === "COMPLETED" && completedIntegration) {
            setIntegration(completedIntegration); setPhase("complete");
            addChat({ role: "ai", text: "Integration build complete! Review the summary below." });
            clearPersistedState();
        } else if (data.status === "FAILED" || data.status === "CANCELLED") {
            setError(data.output?.error || "Build failed");
            addChat({ role: "ai", text: "The build failed. Please try again with a different URL." });
            clearPersistedState();
            setTaskId(null);
            setPhase("url_input");
        }
    }, [addChat, processFeedback]);

    useEffect(() => {
        if (!taskId || didCatchUp.current) return;
        didCatchUp.current = true;
        Bluelagoon.getTaskStatus({ taskId }).then((res) => {
            const task = res?.data?.data || res?.data;
            if (task) {
                handleTaskUpdate({ type: "task_update", taskId, data: task });
                if (isRecovering) {
                    addChat({ role: "ai", text: "Reconnected — synced latest progress." });
                    setIsRecovering(false);
                }
            } else if (isRecovering) {
                // Task no longer exists — stale localStorage
                addChat({ role: "ai", text: "Previous build expired. Start a new one." });
                clearPersistedState(); setTaskId(null); setPhase("url_input"); setIsRecovering(false);
            }
        }).catch(() => {
            if (isRecovering) setIsRecovering(false);
        });
    }, [taskId, handleTaskUpdate, isRecovering, addChat]);

    const wsStatus = useTaskWebSocket({ taskId, onUpdate: handleTaskUpdate, enabled: !!taskId && phase !== "complete" });

    // Persist state to localStorage on every meaningful change (debounced via effect)
    useEffect(() => {
        if (isEditing || !taskId || phase === "url_input") return;
        persistState({ phase, apiType, endpoints, auth, errorMap, integration, draftId, chatMessages: chatMessages.slice(-50), feedbackIdx: feedbackIdx.current, taskStartedAt });
    }, [phase, apiType, endpoints, auth, errorMap, integration, draftId, chatMessages, taskId, isEditing]);

    const submitUrl = useCallback(async (url, visibility) => {
        if (!url) return;
        setError(null);
        didCatchUp.current = false;
        addChat({ role: "user", text: url });

        // Create a draft first so work is never lost
        let currentDraftId = null;
        try {
            const draftRes = await Bluelagoon.saveDraftIntegration({ name: "Building...", baseUrl: url, visibility });
            const draftBody = draftRes?.data?.data || draftRes?.data;
            if (draftBody?.integration?.id) {
                currentDraftId = draftBody.integration.id;
                setDraftId(currentDraftId);
                localStorage.setItem(DRAFT_ID_KEY, currentDraftId);
            }
        } catch (draftErr) {
            const errData = draftErr?.response?.data?.data || draftErr?.response?.data;
            if (errData?.error === "MONTHLY_LIMIT_REACHED") {
                setError(errData.message);
                addChat({ role: "ai", text: errData.message });
                setPhase("url_input");
                return;
            }
            // Other draft errors — continue without draft, publish will create new
        }

        addChat({ role: "ai", text: "Starting build... I'll scrape the docs and discover endpoints." });
        setPhase("crawling");
        try {
            const res = await Bluelagoon.startIntegrationBuild({ url, draftId: currentDraftId });
            const body = res?.data?.data || res?.data;
            if (body?.taskId) {
                setTaskId(body.taskId);
                setTaskStartedAt(Date.now());
                localStorage.setItem(TASK_ID_KEY, body.taskId);
                if (body.deduplicated) addChat({ role: "ai", text: "Resuming an existing build for this URL..." });
            } else {
                const msg = body?.message || "Failed to start build";
                setError(msg); addChat({ role: "ai", text: msg }); setPhase("url_input");
            }
        } catch (err) {
            setError(err.message); addChat({ role: "ai", text: `Error: ${err.message}` }); setPhase("url_input");
        }
    }, [addChat]);

    const reset = useCallback(() => {
        clearPersistedState();
        setTaskId(null);
        setTaskStartedAt(null);
        setDraftId(null);
        setPhase("url_input");
        setEndpoints([]);
        setRevealedEndpoints(0);
        setAuth(null);
        setErrorMap([]);
        setRevealedErrors(0);
        setIntegration(null);
        setError(null);
        setChatMessages([{ role: "ai", text: "Paste an API documentation URL and I'll build a complete integration for you." }]);
        feedbackIdx.current = 0;
        didCatchUp.current = false;
        setIsRecovering(false);
    }, []);

    // Setters for when the agent updates integration data via chat
    const setEndpointsFromAgent = useCallback((newEndpoints) => {
        const mapped = newEndpoints.map((ep, i) => ({ ...ep, id: ep.id || `ep_agent_${Date.now()}_${i}` }));
        setEndpoints(mapped);
        setRevealedEndpoints(mapped.length);
    }, []);

    const setAuthFromAgent = useCallback((newAuth) => { setAuth(newAuth); }, []);

    const setApiInfoFromAgent = useCallback((updated) => {
        if (updated.apiType) setApiType(updated.apiType);
        setIntegration((prev) => prev ? { ...prev, ...updated } : updated);
    }, []);

    return {
        taskId, draftId, phase, apiType, endpoints, revealedEndpoints, auth, errorMap,
        revealedErrors, chatMessages, integration, error, submitUrl, addChat, setPhase, reset,
        setEndpointsFromAgent, setAuthFromAgent, setApiInfoFromAgent, taskStartedAt,
        isConnected: wsStatus.isConnected, isPolling: wsStatus.isPolling, connectionFailed: wsStatus.connectionFailed, isRecovering,
    };
}
